
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface ExchangeRate {
  id: string;
  currency_pair: string;
  rate_type: string;
  rate: number;
  effective_date: string;
  bank_account_id: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
}

export interface CreateExchangeRateInput {
  currency_pair: string;
  rate_type: string;
  rate: number;
  effective_date: string;
  bank_account_id?: string | null;
  notes?: string | null;
}

export const CURRENCY_PAIRS = [
  'USD/ETB', 'EUR/ETB', 'GBP/ETB',
  'USD/EUR', 'EUR/USD',
  'USD/GBP', 'GBP/USD',
  'EUR/GBP', 'GBP/EUR',
] as const;
export const RATE_TYPES = [
  { value: 'bank_rate', label: 'Bank Rate' },
  { value: 'competitive_rate', label: 'Competitive Rate' },
] as const;

export function useExchangeRates(filters?: {
  currency_pair?: string;
  rate_type?: string;
  date_from?: string;
  date_to?: string;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const ratesQuery = useQuery({
    queryKey: ['exchange-rates', filters],
    queryFn: async () => {
      const PAGE = 1000;
      const all: ExchangeRate[] = [];

      for (let from = 0; from < 20000; from += PAGE) {
        let query = supabase
          .from('exchange_rates' as any)
          .select('*')
          .order('effective_date', { ascending: false })
          .order('currency_pair')
          .order('rate_type')
          .range(from, from + PAGE - 1);

        if (filters?.currency_pair) {
          query = query.eq('currency_pair', filters.currency_pair);
        }
        if (filters?.rate_type) {
          query = query.eq('rate_type', filters.rate_type);
        }
        if (filters?.date_from) {
          query = query.gte('effective_date', filters.date_from);
        }
        if (filters?.date_to) {
          query = query.lte('effective_date', filters.date_to);
        }

        const { data, error } = await query;
        if (error) throw error;
        const page = (data || []) as unknown as ExchangeRate[];
        all.push(...page);
        if (page.length < PAGE) break;
      }

      return all;
    },
    enabled: !!user,
  });

  const createMutation = useMutation({
    mutationFn: async (input: CreateExchangeRateInput) => {
      const { data, error } = await supabase
        .from('exchange_rates' as any)
        .insert({
          ...input,
          created_by: user?.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data as unknown as ExchangeRate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchange-rates'] });
      toast.success('Exchange rate added');
    },
    onError: (error: any) => {
      if (error.message?.includes('idx_exchange_rates_unique')) {
        toast.error('A rate for this combination already exists');
      } else {
        toast.error('Failed to add rate: ' + error.message);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('exchange_rates' as any)
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchange-rates'] });
      toast.success('Exchange rate deleted');
    },
    onError: (error: any) => {
      toast.error('Failed to delete rate: ' + error.message);
    },
  });

  return {
    rates: ratesQuery.data || [],
    isLoading: ratesQuery.isLoading,
    error: ratesQuery.error,
    createRate: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    deleteRate: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}

/** Get the applicable exchange rate for a specific date (most recent on or before that date) */
export function useRateForDate(
  currencyPair: string,
  rateType: string,
  date: string,
  enabled = true
) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['exchange-rate-for-date', currencyPair, rateType, date],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('exchange_rates' as any)
        .select('rate, effective_date')
        .eq('currency_pair', currencyPair)
        .eq('rate_type', rateType)
        .lte('effective_date', date)
        .is('bank_account_id', null)
        .order('effective_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as unknown as { rate: number; effective_date: string } | null;
    },
    enabled: !!user && !!currencyPair && !!rateType && !!date && enabled,
  });
}
